const zero = '0'
const base9 = '123456789'
const baseMinus9 = '➊➋➌➍➎➏➐➑➒'
// const baseMinus9 = '⑨⑧⑦⑥⑤④③②①'
const base26 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const baseMinus26 = '🅐🅑🅒🅓🅔🅕🅖🅗🅘🅙🅚🅛🅜🅝🅞🅟🅠🅡🅢🅣🅤🅥🅦🅧🅨🅩'
// const baseMinus26 = 'ⒶⒷⒸⒹⒺⒻⒼⒽⒾⒿⓀⓁⓂⓃⓄⓅⓆⓇⓈⓉⓊⓋⓌⓍⓎⓏ'
// const base32cz = 'ABCČDĎEFGHIJKLMNŇOPQRŘSŠTŤUVXYZŽ'
// const base36cz = 'AÁBCČDĎEÉFGHIÍJKLMNŇOÓPQRŘSŠTŤUÚVXZŽ'
// const base42 = [ 'A', 'Á', 'B', 'C', 'Č', 'D', 'Ď', 'E', 'É', 'Ě', 'F', 'G', 'H', 'Ch', 'I', 'Í', 'J', 'K', 'L', 'M', 'N', 'Ň', 'O', 'Ó', 'P', 'Q', 'R', 'Ř', 'S', 'Š', 'T', 'Ť', 'U', 'Ú', 'Ů', 'V', 'W', 'X', 'Y', 'Ý', 'Z', 'Ž' ]

const defaultCharsArray: string[] = [
	...Array.from(baseMinus26).reverse(),
	...Array.from(baseMinus9).reverse(),
	zero,
	...Array.from(base9),
	...Array.from(base26),
]

export const defaultChars: string = defaultCharsArray.join('')

export type Radix = {
	name: string,
	radix: bigint,
	system: 'standard' | 'bijective' | 'balanced' | 'clock' | 'sum' | 'balsum',
	chars: string,
	enabled: boolean,
	values: Map<string, bigint>,
	reversedValues: Map<bigint, string>,
	low: number,
	high: number,
}

export function createRadixes(chars?: string): Radix[] {
	return [ ...Array<undefined>(35) ].flatMap((_, i) => {
		const radix = i + 2
		const ret = [ createRadix(radix, 'standard', chars) ]
		if (radix < 36) ret.push(createRadix(radix, 'bijective', chars))
		if (radix & 1) ret.push(createRadix(radix, 'balanced', chars))
		if (radix <= 26) ret.push(createRadix(radix, 'sum', chars))
		// if (radix <= 27 && radix & 1) ret.push(createRadix(radix, 'balsum', charsArray))
		// if ((radix & 1) === 0) ret.push(createRadix(radix, 'clock', chars))
		return ret
	})
}

export function createRadix(radix: number, system: Radix['system'] = 'standard', chars = defaultChars, enabled?: boolean, name?: string, allChars = true): Radix {
	if (allChars) {
		if (chars !== defaultChars && chars.length < defaultChars.length) throw new Error(`chars must have at least ${defaultChars.length} characters, ${chars.length} provided`)
		if ((chars.length & 1) === 0) throw new Error('chars must have odd number of characters')
	}
	if (Array.from(chars).length !== new Set(chars).size) {
		throw new Error('chars must be unique')
	}

	let ret: Radix
	const charsArray = Array.from(chars)

	switch (system) {
		case 'standard':
			ret = createStandardRadix(radix, charsArray, enabled, name, allChars)
			break
		case 'bijective':
			ret = createBijectiveRadix(radix, charsArray, enabled, name, allChars)
			break
		case 'balanced':
			ret = createBalancedRadix(radix, charsArray, enabled, name, allChars)
			break
		case 'sum':
			ret = createSumRadix(radix, charsArray, enabled, name, allChars)
			break
		case 'balsum':
			ret = createBalsumRadix(radix, charsArray, enabled, name, allChars)
			break
		case 'clock':
			ret = createClockRadix(radix, charsArray, enabled, name, allChars)
			break
		default:
			throw new Error(`createRadix: Unknown system: "${String(system)}"`)
	}

	return ret
}

function createStandardRadix(radix: number, chars = defaultCharsArray, enabled?: boolean, name = `${radix}`, allChars = true): Radix {
	if (allChars) {
		const zeroAt = (chars.length - 1) / 2
		chars = radix === 26 ? chars.slice(zeroAt + 10, zeroAt + 10 + radix) : chars.slice(zeroAt, zeroAt + radix)
	} else if (chars.length !== radix) throw invalidNumberOfCharacters(name, radix, chars.length)

	const values = chars.map((c, i) => [ c, BigInt(i) ] as [ string, bigint ])

	return {
		name,
		system: 'standard',
		radix: BigInt(radix),
		chars: chars.join(''),
		values: new Map(values),
		reversedValues: new Map(values.map(([ c, v ]) => [ v, c ])),
		low: 0,
		high: radix - 1,
		enabled: enabled ?? [ 2, 10, 12, 26 ].includes(radix),
	}
}

function createBijectiveRadix(radix: number, chars = defaultCharsArray, enabled?: boolean, name = `bij-${radix}`, allChars = true): Radix {
	if (allChars) {
		const zeroAt = (chars.length - 1) / 2
		chars = radix === 26 ? chars.slice(zeroAt, zeroAt + radix + 10).toSpliced(1, 9) : chars.slice(zeroAt, zeroAt + radix + 1)
	} else if (chars.length !== radix + 1) throw invalidNumberOfCharacters(name, radix + 1, chars.length)

	const values = chars.map((c, i) => [ c, BigInt(i) ] as [ string, bigint ])

	return {
		name,
		system: 'bijective',
		radix: BigInt(radix),
		chars: chars.join(''),
		values: new Map(values),
		reversedValues: new Map(values.map(([ c, v ]) => [ v, c ])),
		low: 1,
		high: radix,
		enabled: enabled ?? [ 26 ].includes(radix),
	}
}

function createBalancedRadix(radix: number, chars = defaultCharsArray, enabled?: boolean, name = `bal-${radix}`, allChars = true): Radix {
	if ((radix & 1) === 0) throw new Error(`createRadix: Radix(balanced) must be odd: ${radix}`)
	if (!allChars && chars.length !== radix) throw invalidNumberOfCharacters(name, radix, chars.length)

	const zeroAt = (chars.length - 1) / 2
	const half = (radix - 1) / 2
	const zeroChar = chars[zeroAt]
	chars = radix === 27 && allChars ? chars.slice(zeroAt + 10, zeroAt + 36).toSpliced(13, 0, zeroChar) : chars.slice(zeroAt - half, zeroAt + half + 1)
	const values = chars.map((c, i) => [ c, BigInt(-half + i) ] as [ string, bigint ])

	return {
		name,
		system: 'balanced',
		radix: BigInt(radix),
		chars: chars.join(''),
		values: new Map(values),
		reversedValues: new Map(values.map(([ c, v ]) => [ v, c ])),
		low: -half,
		high: half,
		enabled: enabled ?? [ 3, 13, 19, 27 ].includes(radix),
	}
}

function createSumRadix(radix: number, chars = defaultCharsArray, enabled?: boolean, name = `sum-${radix}`, allChars = true): Radix {
	if (allChars) {
		chars = chars.slice((chars.length - 1) / 2).toSpliced(1, 9)
	}
	if (chars.length < radix) throw invalidNumberOfCharacters(name, radix, chars.length, true)

	const r = radix - 1
	let order = 1
	const values = [
		[ chars[0], 0n ],
		...chars.slice(1).map((c, i) => {
			if (i % r === 0 && i > 0) order *= r + 1
			return [ c, BigInt(((i % r) + 1) * order) ] as [ string, bigint ]
		}),
	] as [ string, bigint ][]

	return {
		name,
		system: 'sum',
		radix: BigInt(radix),
		chars: chars.join(''),
		values: new Map(values),
		reversedValues: new Map(values.reverse().map(([ c, v ]) => [ v, c ])),
		low: 1,
		high: radix,
		enabled: enabled ?? [ 2, 10 ].includes(radix),
	}
}

function createBalsumRadix(radix: number, chars = defaultCharsArray, enabled?: boolean, name = `balsum-${radix}`, allChars = true): Radix {
	let half = (chars.length - 1) / 2
	if (allChars) {
		chars = chars.slice(half + 10).toSpliced((half - 9) / 2, 0, chars[half])
		half = (chars.length - 1) / 2
	}
	if (chars.length < radix) throw invalidNumberOfCharacters(name, radix, chars.length, true)

	const high = (radix - 1) / 2
	let createValues: (order: number) => (c: string, i: number) => [string, bigint]
	if (radix === 3) {
		createValues = (order: number) => (c: string, i: number) => [ c, BigInt(3 ** i * order) ]
	} else {
		createValues = (order: number) => (c: string, i: number) => {
			if (i > 0 && i % high === 0) order *= radix
			return [ c, BigInt(((i % high) + 1) * order) ]
		}
	}

	const plusValues = chars.slice(half + 1).map(createValues(1))
	const minusValues = chars.slice(0, half).toReversed().map(createValues(-1))
	const values = [ ...minusValues.toReversed(), [ chars[half], 0n ], ...plusValues ] as [ string, bigint ][]

	return {
		name,
		system: 'balsum',
		radix: BigInt(radix),
		chars: chars.join(''),
		values: new Map(values),
		reversedValues: new Map(values.map(([ c, v ]) => [ v, c ])),
		low: -high,
		high,
		enabled: enabled ?? [ 3, 27 ].includes(radix),
	}
}

function createClockRadix(radix: number, chars = defaultCharsArray, enabled?: boolean, name = `clock-${radix}`, allChars = true): Radix {
	if (radix & 1) throw new Error(`createRadix: Radix(clock) must be even: ${radix}`)
	let zeroAt: number
	if (allChars) {
		zeroAt = (chars.length - 1) / 2
	} else {
		if (chars.length !== radix) throw invalidNumberOfCharacters(name, radix, chars.length)
		zeroAt = chars.length / 2 - 1
	}

	const half = radix / 2
	chars = chars.slice(zeroAt - half + 1, zeroAt + half + 1)
	const values = chars.map((c, i) => [ c, BigInt(-half + 1 + i) ] as [string, bigint])

	return {
		name,
		system: 'clock',
		radix: BigInt(radix),
		chars: chars.join(''),
		values: new Map(values),
		reversedValues: new Map(values.map(([ c, v ]) => [ v, c ])),
		low: -half + 1,
		high: half,
		enabled: enabled ?? [ 2, 4, 6, 8, 10, 12, 16, 18, 20, 22, 24, 30, 36 ].includes(radix),
	}
}

// const num2strCache = new Map<Radix, Map<bigint, string>>()
export function num2str(num: bigint, radix: Radix, maxLength = 28): string {
	if (num === 0n) return radix.reversedValues.get(0n)!
	// const str = num2strCache.get(radix)?.get(num)
	// if (str != undefined) {
	// 	return str
	// }

	const sum = radix.system === 'sum'
	const bal = radix.system === 'balanced'
	const balsum = radix.system === 'balsum'
	const clock = radix.system === 'clock'

	const ret: string[] = []

	const neg = num < 0n
	let n = neg ? -num : num

	if (sum) {
		let i: number
		let v: string | undefined
		const { reversedValues } = radix
		for (const value of reversedValues.keys()) {
			i = 0
			if (value) {
				while (n >= value) {
					if ((v = reversedValues.get(value))) ret.push(v)
					if (++i === maxLength) {
						ret.unshift('…')
						n %= value
					} else {
						n -= value
					}
				}
			}
		}
	// } else if (balsum) {
	// 	let i
	// 	const max = 5
	// 	let v: string | undefined
	// 	const values = radix.reversedValues
	// 	for (const value of neg ? values.keys() : Array(values.keys()).reverse()) {
	// 		if (!value) continue
	// 		i = 0
	// 		while (neg ? num <= value : num >= value) {
	// 			if (v = values.get(value)) ret.push(v)
	// 			if (++i === max) {
	// 				ret.unshift('…')
	// 				num = neg ? -(num % value) : num % value
	// 			} else {
	// 				num = neg ? num + value : num - value
	// 			}
	// 		}
	// 	}
	} else {
		const { radix: rad, system, reversedValues } = radix

		const bij = system === 'bijective'
		const high = BigInt(radix.high)

		// let d: bigint
		// let q: bigint
		// let i = 1n
		for (let d: bigint, q: bigint, i = 1n; n > 0n; i *= rad) {
			d = n % rad
			if (bij) {
				q = d === 0n ? n / rad - 1n : n / rad
				d = n - q * rad
				n = q
			} else {
				if (bal || clock || balsum) {
					if (d > high || clock && neg && d === high) {
						d -= rad
						n += high
					}
					if (neg) d = -d
				}
				n /= rad
			}
			if (balsum) {
				if (d) ret.unshift(reversedValues.get(d * i)!)
			} else {
				ret.unshift(reversedValues.get(d)!)
			}
		}
	}

	if (neg && !(bal || clock || balsum)) ret.unshift('-')

	const str = ret.join('')
	// let rc = num2strCache.get(radix)
	// if (!rc) {
	// 	num2strCache.set(radix, rc = new Map())
	// }
	// rc.set(num, str)
	return str
}

export function str2num(str: string, radix: Radix): bigint {
	if (str === radix.reversedValues.get(0n)) return 0n

	const neg = str.startsWith('-')
	const s = neg ? str.slice(1) : str

	const { radix: rad, values } = radix
	const sum = radix.system === 'sum'
	const balsum = radix.system === 'balsum'
	const ret = Iterator.from(s).reduce((acc, c) => {
		if (c === '…') return acc
		const v = values.get(c)
		if (v == undefined) throw new Error(`Non-Base character encountered: "${c}". ${allowedCharaters(radix)}`)
		return sum || balsum ? acc + v : acc * rad + v
	}, 0n)

	return neg ? -ret : ret
}

function invalidNumberOfCharacters(name: string, requiredLength: number, providedLength: number, atLeast = false) {
	return new Error(`radix ${name} needs${atLeast ? ' at least' : ''} ${requiredLength} characarters, ${providedLength} provided`)
}

export function allowedCharaters(radix: Radix): string {
	const chars = radix.system === 'bijective' ? radix.chars.slice(1) : radix.chars
	return `Allowed characters are "${radix.system === 'balanced' ? chars : `-${radix.chars}`}".`
}
